"use client";

import { createContext, useContext, useState, useEffect } from "react";
import { useResponsiveLayout, BREAKPOINTS } from "@/hooks/useResponsiveLayout";

const PreviewModeContext = createContext();

export const PREVIEW_WIDTHS = {
  desktop: "max-w-7xl", // Default max width
  tablet: "max-w-3xl", // ~768px
  mobile: "max-w-md", // ~480px
};

export function PreviewModeProvider({ children }) {
  const [previewMode, setPreviewMode] = useState(null);
  const { windowWidth } = useResponsiveLayout();

  useEffect(() => {
    // Check if there's a saved preference in localStorage
    const savedPreviewMode = localStorage.getItem("preferredPreviewMode");

    if (savedPreviewMode) {
      setPreviewMode(savedPreviewMode);
    } else {
      // If no preference, set to null (use native device view)
      setPreviewMode(null);
    }
  }, []);

  // Get the actual device width class even when in preview mode
  const getNativeDeviceClass = () => {
    if (windowWidth <= BREAKPOINTS.mobile) return PREVIEW_WIDTHS.mobile;
    if (windowWidth <= BREAKPOINTS.tablet) return PREVIEW_WIDTHS.tablet;
    return PREVIEW_WIDTHS.desktop;
  };

  // Get the appropriate width class based on preview mode or native device
  const getWidthClass = () => {
    if (!previewMode) {
      return getNativeDeviceClass();
    }
    return PREVIEW_WIDTHS[previewMode] || PREVIEW_WIDTHS.desktop;
  };

  const widthClass = getWidthClass();

  return (
    <PreviewModeContext.Provider
      value={{
        previewMode,
        setPreviewMode,
        widthClass,
        nativeDeviceClass: getNativeDeviceClass(),
      }}
    >
      {children}
    </PreviewModeContext.Provider>
  );
}

export const usePreviewMode = () => useContext(PreviewModeContext);
